import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";
import {
  Camera, Aperture, Sun, Mic, Plane, Clapperboard, Loader2, CheckCircle2,
} from "lucide-react";

const inputCls = "mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50";
const labelCls = "text-[11px] font-medium uppercase tracking-wider text-faint";

// Ticket 2 (2026-02), founder-approved mapping — the SAME six equipment fields freelancers'
// own profile edit already uses (FreelancerPortal.jsx's EQUIPMENT_FIELDS), just revealed
// role-conditionally here based on the union of Primary Role + any Secondary Roles picked.
// Producer/Creative Director/Copywriter/Other reveal nothing, by design.
const ROLE_EQUIPMENT_MAP = {
  Cinematographer: ["camera_owned", "lenses", "lighting"],
  Photographer: ["camera_owned", "lenses", "lighting"],
  "Drone Operator": ["drone", "camera_owned"],
  "FPV/Drone Pilot": ["drone", "camera_owned"],
  Editor: ["editing_setup"],
  "Video Editor": ["editing_setup"],
  Colorist: ["editing_setup"],
  "DI-Colorist": ["editing_setup"],
  "VFX Artist": ["editing_setup"],
  "Sound Recordist": ["audio"],
  Gaffer: ["lighting"],
  "Voice Over Artist": ["audio"],
  Creator: ["camera_owned", "editing_setup"],
};
const EQUIPMENT_META = {
  camera_owned: { label: "Camera Owned", icon: Camera },
  lenses: { label: "Lenses", icon: Aperture },
  lighting: { label: "Lighting", icon: Sun },
  audio: { label: "Audio", icon: Mic },
  drone: { label: "Drone", icon: Plane },
  editing_setup: { label: "Editing Setup", icon: Clapperboard },
};
const LINK_FIELDS = [
  ["work_links", "Work Links (reel, portfolio, samples)"],
  ["website", "Website"],
  ["instagram", "Instagram"],
  ["youtube", "YouTube"],
  ["vimeo", "Vimeo"],
  ["behance", "Behance"],
];

const EMPTY_FORM = {
  name: "", registered_gmail: "", phone: "", city: "", country: "",
  primary_role: "", secondary_roles: [],
  experience_years: "", languages: "", bio: "", skills: "", achievements: "",
  work_links: "", website: "", instagram: "", youtube: "", vimeo: "", behance: "",
  camera_owned: "", drone: "", lenses: "", lighting: "", audio: "", editing_setup: "",
};

export default function BuildApply() {
  const [options, setOptions] = useState({ primary_role: [], secondary_roles: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/v1/build/apply/field-options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const equipmentFields = useMemo(() => {
    const roles = [form.primary_role, ...form.secondary_roles].filter(Boolean);
    const keys = new Set();
    roles.forEach((r) => (ROLE_EQUIPMENT_MAP[r] || []).forEach((k) => keys.add(k)));
    return [...keys];
  }, [form.primary_role, form.secondary_roles]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleSecondary = (role) => {
    setForm((f) => ({
      ...f,
      secondary_roles: f.secondary_roles.includes(role)
        ? f.secondary_roles.filter((r) => r !== role)
        : [...f.secondary_roles, role],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.registered_gmail.trim() || !form.primary_role) {
      setError("Full Name, Email, and Primary Role are required.");
      return;
    }
    if (!LINK_FIELDS.some(([key]) => form[key].trim())) {
      setError("Share at least one way to see your work — a link, your Instagram, YouTube, or similar.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.experience_years = form.experience_years === "" ? undefined : Number(form.experience_years);
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] === undefined) delete payload[k]; });
      const r = await api.post("/v1/build/apply", payload);
      setResult(r.data);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      if (detail?.code === "missing_link") setError("Share at least one way to see your work — a link, your Instagram, YouTube, or similar.");
      else if (detail?.code === "invalid_registered_gmail") setError("Enter a valid email address.");
      else if (detail?.code === "invalid_primary_role") setError("Choose a valid Primary Role from the list.");
      else setError("Something went wrong submitting your application — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4" data-testid="build-apply-success">
        <div className="w-full max-w-md text-center">
          <Logo theme="light" size="lg" iconOnly showBuildLabel />
          <CheckCircle2 className="h-10 w-10 text-brand mx-auto mt-8" />
          <h1 className="font-editorial text-2xl text-ink mt-4">
            {result.duplicate ? "Already on file" : "Application received"}
          </h1>
          <p className="mt-2 text-sm text-ink-soft" data-testid="build-apply-success-message">{result.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas" data-testid="build-apply-page">
      <div className="border-b border-hairline bg-paper px-4 sm:px-8 py-6">
        <Logo theme="light" size="lg" iconOnly showBuildLabel />
      </div>
      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 sm:px-8 py-10 space-y-8" data-testid="build-apply-form">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Talent Call</div>
          <h1 className="font-editorial text-3xl text-ink mt-1">Apply to NJM Build</h1>
          <p className="mt-2 text-sm text-ink-soft">Tell us about yourself and share your work — we review every application.</p>
        </div>

        {/* Basic Info */}
        <section className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Full Name *</label>
              <input data-testid="build-apply-field-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input data-testid="build-apply-field-registered_gmail" type="email" className={inputCls} value={form.registered_gmail}
                onChange={(e) => set("registered_gmail", e.target.value)} placeholder="you@gmail.com" />
              <p className="mt-1 text-[11px] text-faint">Use the Google email you'll sign in with.</p>
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="build-apply-field-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>City</label>
              <input data-testid="build-apply-field-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Country</label>
              <input data-testid="build-apply-field-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} />
            </div>
          </div>
        </section>

        {/* Role */}
        <section className="space-y-4">
          <div>
            <label className={labelCls}>Primary Role *</label>
            <select data-testid="build-apply-field-primary_role" className={inputCls} value={form.primary_role}
              onChange={(e) => set("primary_role", e.target.value)}>
              <option value="">Select your primary role</option>
              {options.primary_role.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Secondary Roles</label>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="build-apply-field-secondary_roles">
              {options.secondary_roles.filter((r) => r !== form.primary_role).map((r) => (
                <label key={r} className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-2.5 py-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.secondary_roles.includes(r)} data-testid={`build-apply-secondary-role-${r}`}
                    onChange={() => toggleSecondary(r)} />
                  {r}
                </label>
              ))}
            </div>
          </div>
        </section>

        {/* Equipment — role-conditional */}
        {equipmentFields.length > 0 && (
          <section className="space-y-4 rounded-lg border border-hairline bg-paper/60 p-4" data-testid="build-apply-equipment-section">
            <div className={labelCls}>Equipment</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {equipmentFields.map((key) => {
                const meta = EQUIPMENT_META[key];
                const Icon = meta.icon;
                return (
                  <div key={key}>
                    <label className={labelCls}><Icon className="inline h-3 w-3 mr-1 -mt-0.5" />{meta.label}</label>
                    <input data-testid={`build-apply-field-${key}`} className={inputCls} value={form[key]} onChange={(e) => set(key, e.target.value)} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Experience & Bio */}
        <section className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Years of Experience</label>
              <input data-testid="build-apply-field-experience_years" type="number" min="0" className={inputCls}
                value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Languages</label>
              <input data-testid="build-apply-field-languages" className={inputCls} value={form.languages} onChange={(e) => set("languages", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Bio</label>
            <textarea data-testid="build-apply-field-bio" rows={3} className={inputCls} value={form.bio} onChange={(e) => set("bio", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Skills (comma-separated)</label>
            <input data-testid="build-apply-field-skills" className={inputCls} value={form.skills} onChange={(e) => set("skills", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Achievements (notable projects, awards)</label>
            <textarea data-testid="build-apply-field-achievements" rows={2} className={inputCls} value={form.achievements} onChange={(e) => set("achievements", e.target.value)} />
          </div>
        </section>

        {/* Portfolio Links */}
        <section className="space-y-4">
          <div className={labelCls}>Your Work — at least one required *</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {LINK_FIELDS.map(([key, label]) => (
              <div key={key} className={key === "work_links" ? "sm:col-span-2" : ""}>
                <label className={labelCls}>{label}</label>
                {key === "work_links" ? (
                  <textarea data-testid={`build-apply-field-${key}`} rows={2} className={inputCls} value={form[key]} onChange={(e) => set(key, e.target.value)} />
                ) : (
                  <input data-testid={`build-apply-field-${key}`} className={inputCls} value={form[key]} onChange={(e) => set(key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </section>

        {error && <div data-testid="build-apply-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}

        <button
          type="submit" disabled={submitting} data-testid="build-apply-submit-btn"
          className="w-full rounded-md bg-brand py-3 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitting ? "Submitting…" : "Submit Application"}
        </button>
      </form>
    </div>
  );
}
